<template>
  <div class="relative filter-menu-container inline-block z-40" ref="reference">
    <button 
      @click="toggleMenu" 
      class="p-2 rounded-md hover:bg-gray-100 focus:outline-none flex items-center"
      :class="{ 'bg-gray-100': isOpen }"
    >
      <span :class="option.iconClass">{{ option.icon }}</span> 
      <span v-if="option.showText" class="ml-1 text-sm">{{ option.text }}</span>
      <span v-if="badgeCount > 0 && !isDefaultSelection" class="ml-1 text-xs bg-primary text-white rounded-full w-5 h-5 flex items-center justify-center">
        {{ badgeCount }}
      </span>
    </button>
    
    <Teleport to="body">
      <transition name="fade">
        <div 
          v-if="isOpen" 
          ref="floating" 
          :style="floatingStyles" 
          class="border rounded-md shadow-xl bg-white text-sm max-h-80 overflow-y-auto thin-scrollbar floating-dropdown"
        >
          <!-- Filter title -->
          <div class="px-3 py-2 border-b border-gray-200 font-medium sticky top-0 bg-white">
            {{ computedTitle }}
          </div>
          
          <!-- Search input for filters with many options -->
          <div v-if="option.items && option.items.length > 8" class="px-3 py-2 border-b border-gray-200 sticky top-9 bg-white">
            <custom-input 
              v-model="searchQuery" 
              type="text" 
              placeholder="{{ t('global.searchFilters') }}..." 
              class="w-full text-xs"
              size="sm"
              icon="search"
              :show-icon="true"
              :show-clear-button="searchQuery.length > 0"
            />
          </div>
          
          <!-- Filter items -->
          <div 
            v-for="item in filteredItems" 
            :key="item.id" 
            @click="handleSelect(item.id)" 
            class="px-3 py-2 hover:bg-gray-100 cursor-pointer flex justify-between items-center whitespace-nowrap"
          >
            <div class="flex items-center">
              <!-- Use checkbox for multi-select filters -->
              <VCheckbox 
                v-if="option.multiSelect" 
                :modelValue="isItemSelected(item.id)" 
                @update:modelValue="handleSelect(item.id)" 
                color="primary" 
                class="!m-0 mr-2"
                @click.stop
              />
              <span>{{ item.name }}</span>
            </div>
            
            <!-- Show check mark for single-select filters -->
            <span v-if="!option.multiSelect && isItemSelected(item.id)" class="material-symbols-outlined text-secondary !text-sm">
              check
            </span>
          </div>
          
          <!-- Empty state if no matches -->
          <div v-if="filteredItems.length === 0" class="px-3 py-4 text-center text-gray-500">
            {{ t('global.noMatchingFilters') }}
          </div>
          
          <!-- Action buttons for multi-select filters -->
          <div v-if="option.multiSelect && computedItems.length > 1" class="px-3 py-2 flex justify-between border-t border-gray-200 sticky bottom-0 bg-white">
            <button 
              @click.stop="clearAllSelections" 
              class="text-xs text-gray-600 hover:text-primary"
            >
              {{ t('global.clearAll') }}
            </button>
            <button 
              @click.stop="selectAllItems" 
              class="text-xs text-primary"
            >
              {{ t('global.selectAll') }}
            </button>
          </div>
          
          <!-- Clear filter option for single-select -->
          <div 
            v-if="!option.multiSelect && badgeCount > 0 && !isDefaultSelection" 
            @click.stop="clearAllSelections" 
            class="px-3 py-2 text-primary hover:bg-gray-100 cursor-pointer border-t border-gray-200"
          >
            {{ t('global.clearFilter') }}
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, inject, ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { computePosition, flip, shift, offset } from '@floating-ui/dom'
import VCheckbox from '@/components/base/inputs/Checkbox/VCheckbox.vue'
import CustomInput from '@/components/base/inputs/VInput.vue'

const props = defineProps({
  option: { type: Object, required: true },
  selected: { type: [Array, String, Number], default: () => [] },
})

const { t } = useI18n()

// Ensure selected is always an array when dealing with array operations
const ensureArray = (value) => {
  if (!value) return []
  return Array.isArray(value) ? value : [value]
}

// Dynamic filter title with i18n support
const computedTitle = computed(() => {
  // If title is a translation key (starts with entity., filter. etc.), translate it
  if (props.option.title && typeof props.option.title === 'string' && 
      (props.option.title.startsWith('entity.') || 
       props.option.title.startsWith('filter.') ||
       props.option.title.startsWith('global.'))) {
    return t(props.option.title)
  }
  return props.option.title || t('global.filter')
})

// Dynamic filter items with i18n support
const computedItems = computed(() => {
  if (!props.option.items) return []
  
  return props.option.items.map(item => {
    // If name is a translation key, translate it
    if (item.name && typeof item.name === 'string' && 
        (item.name.startsWith('entity.') || 
         item.name.startsWith('filter.') ||
         item.name.startsWith('global.'))) {
      return { ...item, name: t(item.name) }
    }
    return item
  })
})

const emit = defineEmits(['select'])

// Local state
const searchQuery = ref('')
const reference = ref(null)
const floating = ref(null)

// Floating UI için stil özellikleri
const floatingStyles = ref({
  position: 'fixed',
  top: '0',
  left: '0',
  width: 'auto',
  zIndex: 9999,
  visibility: 'hidden',
})

// Use shared openMenu from parent
const openMenu = inject('openMenu')

// Compute if this menu is open
const isOpen = computed(() => openMenu.value === `filter-${props.option.id}`)

// Filter items based on search query
const filteredItems = computed(() => {
  if (!searchQuery.value) return computedItems.value
  
  const query = searchQuery.value.toLowerCase()
  return computedItems.value.filter(item => 
    item.name.toLowerCase().includes(query)
  )
})

// Check if an item is selected
const isItemSelected = (itemId) => {
  const selectedArray = ensureArray(props.selected)
  return selectedArray.includes(itemId)
}

// Compute badge count - show number of active filters
const badgeCount = computed(() => {
  const selectedArray = ensureArray(props.selected)
  return selectedArray.length
})

// Check if current selection is the default one
const isDefaultSelection = computed(() => {
  if (!props.selected || (Array.isArray(props.selected) && props.selected.length === 0)) {
    return false
  }
  
  const selectedArray = ensureArray(props.selected)
  if (selectedArray.length !== 1) return false
  
  // Check if the selected item is marked as default
  const selectedItem = computedItems.value.find(item => item.id === selectedArray[0])
  return selectedItem && selectedItem.default
})

// Toggle menu visibility
const toggleMenu = async () => {
  if (isOpen.value) {
    openMenu.value = null
  } else {
    openMenu.value = `filter-${props.option.id}`
    // Wait for menu to be visible before positioning
    nextTick(async () => {
      await updatePosition()
    })
  }
}

// Update dropdown position using Floating UI
const updatePosition = async () => {
  if (!reference.value || !floating.value) return

  try {
    const middleware = [
      offset(4),
      flip({
        fallbackPlacements: ['top-start', 'bottom-start', 'top-end', 'bottom-end'],
        padding: 8,
      }),
      shift({ padding: 8 }),
    ]

    const { x, y } = await computePosition(reference.value, floating.value, {
      placement: 'bottom-start',
      middleware,
    })

    // Get trigger width for setting dropdown width
    const triggerRect = reference.value.getBoundingClientRect()
    const minWidth = Math.max(triggerRect.width, 256) // min 16rem (256px)

    // Update styles
    floatingStyles.value = {
      position: 'fixed',
      top: `${y}px`,
      left: `${x}px`,
      minWidth: `${minWidth}px`,
      zIndex: 9999,
      visibility: 'visible',
    }
  } catch (error) {
    console.error('Error updating position:', error)
  }
}

// Emit selection event
const handleSelect = (itemId) => {
  emit('select', itemId)
}

// Clear all selections
const clearAllSelections = () => {
  // For multi-select, emit events to remove all selected items
  if (props.option.multiSelect) {
    const selectedArray = ensureArray(props.selected)
    selectedArray.forEach(itemId => {
      emit('select', itemId)
    })
  } else {
    // For single-select, find default item
    const defaultItem = computedItems.value.find(item => item.default)
    
    // If there's a default item, select it
    if (defaultItem) {
      emit('select', defaultItem.id)
    } else {
      // If no default, just toggle off current selection
      const selectedValue = Array.isArray(props.selected) && props.selected.length > 0 ? props.selected[0] : props.selected
      if (selectedValue) {
        emit('select', selectedValue)
      }
    }
  }
}

// Select all items
const selectAllItems = () => {
  computedItems.value.forEach(item => {
    if (!isItemSelected(item.id)) {
      emit('select', item.id)
    }
  })
}

// Add event listeners for scroll and resize
onMounted(() => {
  document.addEventListener('scroll', updatePosition, true)
  window.addEventListener('resize', updatePosition)
})

// Clean up event listeners
onBeforeUnmount(() => {
  document.removeEventListener('scroll', updatePosition, true)
  window.removeEventListener('resize', updatePosition)
})

// Watch for openMenu changes
watch(() => openMenu.value, async (newValue) => {
  if (newValue === `filter-${props.option.id}`) {
    await nextTick()
    await updatePosition()
  }
})

// Clear search on menu close
watch(isOpen, (newValue) => {
  if (!newValue) {
    searchQuery.value = ''
  }
})
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

.thin-scrollbar::-webkit-scrollbar {
  width: 4px;
}

.thin-scrollbar::-webkit-scrollbar-track {
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 10px;
}

.thin-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
}

.floating-dropdown {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}
</style>
